// src/components/DocumentGridView.tsx
// Card-based grid view for the Document Workspace.

import { useI18n } from '@react/hooks';
import { Badge, Card, CardHeader, CardContent } from '@react/components';

import {
  getEntityId,
  getDocumentStatus,
  getDocumentOwner,
  getDocumentIcon,
  getStatusMapping,
  documentStatusMap,
  formatRelativeTime,
} from '@/features/document/logic';
import { EntityAvatar } from '@/components/EntityAvatar';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface DocumentGridViewProps {
  documents: any[];
  snippetsById?: Record<string, string>;
  selectedDocId?: string;
  onSelect: (id: string) => void;
  onOpen: (id: string) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DocumentGridView({
  documents,
  snippetsById = {},
  selectedDocId,
  onSelect,
  onOpen,
}: DocumentGridViewProps) {
  const { t, locale } = useI18n();

  if (documents.length === 0) {
    return (
      <div className="py-12 text-center">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
          <svg className="h-6 w-6 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
        </div>
        <p className="text-sm font-medium text-foreground">{t('document.noItems')}</p>
        <p className="mt-1 text-xs text-muted-foreground">{t('document.noItemsDescription')}</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {documents.map((doc: any) => (
        <DocumentCard
          key={getEntityId(doc)}
          document={doc}
          snippet={snippetsById[getEntityId(doc)]}
          isSelected={getEntityId(doc) === selectedDocId}
          onSelect={() => onSelect(getEntityId(doc))}
          onOpen={() => onOpen(getEntityId(doc))}
          locale={locale}
          t={t}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Document card
// ---------------------------------------------------------------------------

interface DocumentCardProps {
  document: any;
  snippet?: string;
  isSelected: boolean;
  onSelect: () => void;
  onOpen: () => void;
  locale: string;
  t: (key: string) => string;
}

function DocumentCard({
  document,
  snippet,
  isSelected,
  onSelect,
  onOpen,
  locale,
  t,
}: DocumentCardProps) {
  const status = getDocumentStatus(document);
  const owner = getDocumentOwner(document);
  const statusInfo = getStatusMapping(documentStatusMap, status);
  const iconPath = getDocumentIcon(document);

  return (
    <Card
      className={`cursor-pointer transition-all hover:shadow-md hover:border-primary/20 ${
        isSelected ? 'ring-2 ring-primary/30 border-primary/30' : ''
      }`}
      onClick={onSelect}
      onDoubleClick={onOpen}
    >
      <CardHeader className="pb-2">
        <div className="flex items-start gap-2.5">
          {/* Type icon */}
          <div className="flex-shrink-0 w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center">
            <svg
              className="h-4 w-4 text-primary"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d={iconPath} />
            </svg>
          </div>

          {/* Title + description */}
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold text-foreground truncate">
              {document.title || t('common.untitled')}
            </h3>
            {document.description && (
              <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                {document.description}
              </p>
            )}
            {snippet && (
              <p className="mt-1 line-clamp-2 text-xs text-primary">
                {t('document.workspace.contentMatch')}: {snippet}
              </p>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        <div className="flex items-center justify-between">
          {/* Status badge */}
          <div className="flex items-center gap-2">
            {status && (
              <Badge variant={statusInfo.variant as any} size="sm">
                {t(statusInfo.labelKey)}
              </Badge>
            )}
          </div>

          {/* Author + date */}
          <div className="flex items-center gap-2">
            {document.updated_at && (
              <span className="text-xs text-muted-foreground">
                {formatRelativeTime(document.updated_at, locale)}
              </span>
            )}
            {owner && (
              <EntityAvatar name={owner} image={document.owner?.avatar} userId={document.owner?.entity?.id} size="xs" className="h-5 w-5 text-[8px]" />
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default DocumentGridView;
